// ============================================================
// src/hooks/useTwilioVoice.ts
// Base : version hooks/hooks/ (httpsCallable Firebase natif)
// Ajout : getCallStatus (manquant dans la version Firebase)
// ============================================================
import { useState, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { callsApi } from '@/services/api/calls.api';
import { toast } from 'sonner';

interface CallState {
  callSid: string | null;
  status: 'idle' | 'initiating' | 'ringing' | 'in-progress' | 'completed' | 'failed' | 'busy' | 'no-answer';
  to: string | null;
  from: string | null;
  duration: number | null;
  startTime: string | null;
}

interface MakeCallParams {
  to: string;
  callerId?: string;
  twimlUrl?: string;
}

export function useTwilioVoice() {
  const [callState, setCallState] = useState<CallState>({
    callSid: null, status: 'idle', to: null,
    from: null, duration: null, startTime: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Firebase Functions — us-central1
  const functions = useMemo(() => getFunctions(undefined, 'us-central1'), []);

  // ── 1. Initier un appel ──────────────────────────────────────

  const makeCall = useCallback(async (params: MakeCallParams): Promise<boolean> => {
    setIsLoading(true);
    setCallState(prev => ({ ...prev, status: 'initiating', to: params.to }));
    try {
      const data: any = await callsApi.initiateCall({ to: params.to });

      if (data?.ok || data?.callId) {
        setCallState({
          callSid: data.twilioCallSid || data.callId, status: 'ringing',
          to: params.to, from: null,
          duration: null, startTime: new Date().toISOString(),
        });
        toast.success('Appel lancé !');
        return true;
      } else {
        throw new Error('Échec de l\'appel');
      }
    } catch (error: any) {
      console.error('Error making call:', error);
      setCallState(prev => ({ ...prev, status: 'failed' }));
      toast.error(`Erreur: ${error.message || 'Échec de l\'appel'}`);
      return false;
    } finally { setIsLoading(false); }
  }, []);

  // ── 2. Statut d'un appel ─────────────────────────────────────
  // Ajout manquant dans la version Firebase originale

  const getCallStatus = useCallback(async (callSid: string): Promise<void> => {
    try {
      const firebaseCallStatus = httpsCallable(functions, 'makeCall');
      const { data }: any = await firebaseCallStatus({ action: 'status', callSid });
      if (data) {
        setCallState(prev => ({
          ...prev,
          status: data.status as CallState['status'],
          duration: data.duration ? parseInt(data.duration) : null,
        }));
      }
    } catch (error) {
      console.error('Error getting call status:', error);
    }
  }, [functions]);

  // ── 3. Terminer un appel ─────────────────────────────────────

  const endCall = useCallback(async (): Promise<boolean> => {
    if (!callState.callSid) {
      toast.error('Aucun appel actif à terminer');
      return false;
    }
    setIsLoading(true);
    try {
      const data: any = await callsApi.endCall(callState.callSid);

      if (data?.ok) {
        setCallState({
          callSid: null, status: 'completed',
          to: null, from: null, duration: null, startTime: null,
        });
        toast.success('Appel terminé');
        return true;
      } else {
        throw new Error('Impossible de raccrocher');
      }
    } catch (error: any) {
      console.error('Error ending call:', error);
      toast.error('Erreur lors de la coupure');
      return false;
    } finally { setIsLoading(false); }
  }, [callState.callSid]);

  // ── Reset ────────────────────────────────────────────────────

  const resetCallState = useCallback(() => {
    setCallState({
      callSid: null, status: 'idle', to: null,
      from: null, duration: null, startTime: null,
    });
  }, []);

  return { callState, isLoading, makeCall, getCallStatus, endCall, resetCallState };
}